import React, { useState } from "react";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
import { DA_Icon, DA_IconNames } from "@danishagro/shared/src/components/atoms/Icon/Icon.component";
import { DA_InlineLink } from "@danishagro/shared/src/components/atoms/InlineLink/InlineLink.component";
import { useTranslations } from "@danishagro/shared/src/contexts/translations/translations.context";
import { animated, useSpring } from "react-spring";
import { DepartmentProps } from "./Department.interface";
// eslint-disable-next-line css-modules/no-unused-class
import S from "./Department.module.scss";

export const Department = (props: DepartmentProps) => {
    const { getDictionaryString } = useTranslations();

    const googleMapsUrl = `http://www.google.com/maps/dir/${props.props?.googleMap?.address?.coordinates?.lat},${props.props?.googleMap?.address?.coordinates?.lng}`;

    const [toggleOpenHours, setToggleOpenHours] = useState(false);

    // Animation for the opening hours content (slide down/up effect)
    const openHoursAnimation = useSpring({
        opacity: toggleOpenHours ? 1 : 0, // Fade in/out
        maxHeight: toggleOpenHours ? "500px" : "0", // Max height for smooth slide down/up
        transform: toggleOpenHours ? "translateY(0)" : "translateY(-20px)", // Slide effect
        overflow: "hidden",
        config: { tension: 200, friction: 25 }, // Customize smoothness
    });

    const handleToggleOpenHours = () => {
        setToggleOpenHours((prev) => !prev);
    };

    return (
        <div
            className={cn(S.wrapper, props.className)}
            onClick={props.onDepartmentChange}
            onKeyDown={props.onDepartmentChange}
            role="presentation"
        >
            <div className={S.content}>
                {/** Address */}
                <div className={S.addressWrapper}>
                    {/** City */}
                    {props.props?.departmentName ? (
                        <strong className={S.city}>{props.props?.departmentName}</strong>
                    ) : null}

                    {/** Address */}
                    {props.props?.address ? <span>{props.props?.address}</span> : null}

                    {/** Zip & City */}
                    {props.props?.zipCode || props.props?.city ? (
                        <span>
                            {props.props?.zipCode} {props.props?.city}
                        </span>
                    ) : null}

                    {/**Phone */}
                    {props.props?.departmentPhone ? (
                        <span className={S.phone}>
                            <DA_InlineLink
                                title={props.props?.departmentPhone}
                                href={`tel:${props.props?.departmentPhone}`}
                                icon={DA_IconNames.PhoneOutline}
                                target="_self"
                                className={S.phoneIcon}
                            />
                        </span>
                    ) : null}

                    {props?.props?.link ? (
                        <DA_InlineLink
                            title={props?.props.link?.name}
                            href={props?.props.link?.url}
                            target={props?.props.link?.target}
                        />
                    ) : null}
                </div>
                {/** Directions */}
                {props.props?.googleMap ? (
                    <div className={S.directionsWrapper}>
                        <DA_InlineLink
                            title={getDictionaryString("Map.ShowRoute")}
                            icon={DA_IconNames.Pin}
                            href={googleMapsUrl}
                        />
                    </div>
                ) : null}
            </div>

            {/** Open & Signup */}
            {props.props?.isHarvest ? (
                <div className={S.signupWrapper}>
                    <span className={S.signupLabel}>
                        {getDictionaryString("Department.OpenForCrops")}
                    </span>
                </div>
            ) : null}

            {/* OpenHours toggle*/}
            {props.props.openingHours?.length > 0 && (
                <div>
                    {toggleOpenHours && (
                        <animated.div style={openHoursAnimation} className={S.openHoursInner}>
                            <ul className={S.openingHoursList}>
                                {props.props.openingHours.map((openHours, index) => {
                                    const openingText = openHours.content.props.text;
                                    const openTime = openHours.content.props.opens;
                                    const closeTime = openHours.content.props.closes;

                                    return (
                                        <li
                                            key={index}
                                            className={`${S.openHoursItem} ${
                                                index % 2 !== 0 ? S.odd : ""
                                            }`}
                                        >
                                            {openingText ? (
                                                <div
                                                    dangerouslySetInnerHTML={{
                                                        __html: openingText,
                                                    }}
                                                />
                                            ) : null}
                                            {openTime || closeTime ? (
                                                <span>
                                                    {openTime} - {closeTime}
                                                </span>
                                            ) : null}
                                        </li>
                                    );
                                })}
                            </ul>
                        </animated.div>
                    )}

                    {/* Toggle Button */}
                    <div
                        onClick={handleToggleOpenHours}
                        className={S.toggleButton}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") handleToggleOpenHours();
                        }}
                        role="button"
                        tabIndex={0}
                    >
                        {toggleOpenHours ? (
                            <>
                                <DA_Icon
                                    name={DA_IconNames.ChevronSmallUp}
                                    className={S.toggleIcon}
                                />
                                {getDictionaryString("Department.OpenHoursHideText")}
                            </>
                        ) : (
                            <>
                                <DA_Icon
                                    name={DA_IconNames.ChevronSmallDown}
                                    className={S.toggleIcon}
                                />
                                {getDictionaryString("Department.OpenHoursShowText")}
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
